<template>
  <ModalBase :name="$options.name" :title="'Review your bid'" @close="$emit('close')">
    <template #body>
      <p v-if="auction.bidsCount" class="flex items-center mb-4 space-x-2 font-medium">
        <span>{{ `Current bid ${formatCurrency(auction.price)}` }}</span>
        <FontAwesomeIcon :icon="faCircle" :style="{ fontSize: '7px' }" />
        <span>{{ `${auction.bidsCount} bid(s)` }}</span>
      </p>

      <UtilInfo
        v-if="outbid || outbidOnMaxPrice"
        :icon="faSirenOn"
        class="mb-4 bg-danger-lighter border-danger text-primary"
      >
        <span class="font-bold">You've been outbid!</span>
        Place another bid to stay in the running.
        <p v-if="(auction.userBid && auction.userBid.priceMax) || outbidOnMaxPrice">
          You will need to raise your maximum bid.
        </p>
        <p v-else>Consider adding a maximum bid to save time on repeat bidding</p>
      </UtilInfo>

      <div class="">
        <p class="mt-2 mb-1 text-gray-darker">
          Enter your maximum bid and we will auto bid for you up to this amount.
        </p>
        <p class="mb-2 text-gray-darker">80% of bidders with a max bid go on to win auctions.</p>

        <div class="flex items-center space-x-2">
          <FormSelect
            name="price"
            label="Your maximum bid"
            v-model="bidApi.model.priceMax"
            :items="maxPriceOptions"
            :emptyFirstOption="false"
          />

          <UtilButton
            color="primary"
            @click="onBidConfirm({ isMaxBid: true })"
            :loading="bidApi.loading"
            class="mt-5"
          >
            Set maximum bid
          </UtilButton>
        </div>
      </div>

      <p class="text-sm font-medium text-gray-600">
        By placing this bid you are committing to buy this livestock under the SellMyLivestock auction
        <UtilTextLink @click="$store.dispatch('modal/showTerms')" class="underline text-brand">
          terms and conditions
        </UtilTextLink>
      </p>

      <div class="flex items-center my-4 space-x-4">
        <div class="flex-auto h-px bg-gray-lighter" />

        <p class="font-medium text-gray-darker">or</p>

        <div class="flex-auto h-px bg-gray-lighter" />
      </div>

      <div>
        <UtilButton color="primary" @click="onBidConfirm({ isMaxBid: false })" :loading="bidApi.loading" block>
          Place single bid for {{ formatCurrency(bid.price) }}
        </UtilButton>
      </div>

      <UtilApiError :models="[bidApi]" showOnlyMessage class="mt-4" />

      <!-- <pre>{{ auction }}</pre> -->
    </template>
  </ModalBase>
</template>

<script>
import { faCircle } from '@fortawesome/pro-solid-svg-icons'
import { faSirenOn } from '@fortawesome/pro-regular-svg-icons'
import ModalBase from '@/components/modals/ModalBase'
import UtilApiError from '@/components/utils/UtilApiError'
import UtilButton from '@/components/utils/UtilButton'
import UtilTextLink from '@/components/utils/UtilTextLink'
import { formatCurrency } from '@/lib/helpers/transform'
import currencyMask from '@/lib/masks/currency'
import BridgeApi from '@/lib/api/bridge'
import FormSelect from '@/components/forms/elements/FormSelect'
import UtilInfo from '@/components/utils/listing/UtilInfo'

export default {
  name: 'ModalAuctionBid',
  components: {
    ModalBase,
    UtilInfo,
    UtilButton,
    UtilApiError,
    FormSelect,
    UtilTextLink
  },
  props: {
    modalData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      bidApi: new BridgeApi({
        price: null,
        priceMax: null
      }),
      outbid: false,
      formatCurrency,
      currencyMask,
      faCircle,
      faSirenOn
    }
  },
  computed: {
    auction() {
      return this.modalData.auction
    },
    bid() {
      return { price: this.modalData.auction.priceNext }
    },
    outbidOnMaxPrice() {
      return this.auction.userMaxBidPrice && this.auction.userMaxBidPrice <= this.auction.price
    },
    confirm() {
      return [
        { label: 'Your bid', value: formatCurrency(this.bid.price), disabled: !!this.bid.max },
        { label: 'Your next bid', value: formatCurrency(this.bid.price), disabled: !this.bid.max },
        { label: 'Your max bid', value: formatCurrency(this.bid.max), disabled: !this.bid.max }
      ].filter(item => item.disabled !== true)
    },

    confirmDetails() {
      return [
        {
          label: 'Your next bid',
          detail: formatCurrency(this.bid.price)
        },
        {
          label: 'Your max bid',
          detail: formatCurrency(this.bidApi.model.priceMax),
          disabled: !this.bidApi.model.priceMax
        }
      ].filter(item => item.disabled !== true)
    },

    maxPriceOptions() {
      let options = [
        {
          text: 'Select a maximum bid',
          value: null
        }
      ]

      // Max price is 10 increments above the reserve price or the current price, whichever is higher
      const maxPriceOption =
        this.auction.price < this.auction.priceReserve
          ? this.auction.priceReserve + this.auction.priceIncrement * 10
          : this.auction.price + this.auction.priceIncrement * 10

      let price = this.auction.priceNext

      for (let i = price; i <= maxPriceOption; i += this.auction.priceIncrement) {
        options.push({
          text: formatCurrency(i),
          value: i
        })
      }

      return options
    }
  },
  watch: {
    '$route.path'() {
      this.closeModal()
    }
  },

  created() {
    this.bidApi.model.price = this.modalData.bid.price || null
  },

  methods: {
    closeModal() {
      this.$emit('close')
    },

    async onBidConfirm({ isMaxBid = false }) {
      this.outbid = false

      if (!this.auction.isOpen) {
        alert('This auction is now closed.')
        this.closeModal()
        return
      }

      if (isMaxBid && !this.bidApi.model.priceMax) {
        alert('Please enter a maximum bid amount.')
        return
      }

      if (!isMaxBid) {
        this.bidApi.model.priceMax = null
      }

      const { listingRef } = this.modalData.listing

      this.bidApi.model = {
        companyRef: this.app.company.companyRef,
        accountRef: this.app.account.accountRef,
        userRef: this.app.user.userRef,
        price: Number(this.modalData.bid.price),
        priceMax: Number(this.bidApi.model.priceMax)
      }

      await this.bidApi.post(`/v1/listings/${listingRef}/bids`, this.bidApi.model)

      console.log('this.bidApi', this.bidApi)

      if (this.bidApi.data?.bidRef || this.bidApi.data?.ref) {
        this.$store.dispatch('auction/log', {
          type: isMaxBid ? 'maxBid' : 'bid',
          data: {
            bid: this.bidApi.model,
            response: this.bidApi.data
          }
        })

        this.$notify.floatSuccess('Your bid has been placed!')

        this.closeModal()
      } else if (this.bidApi.data.outbid) {
        this.outbid = true

        this.$notify.floatError('You have been outbid!')

        this.$store.dispatch('auction/log', {
          type: isMaxBid ? 'maxBid - outbid' : 'bid - outbid',
          data: {
            bid: this.bidApi.model,
            response: this.bidApi.data
          }
        })

        this.bidApi.model.priceMax = null
      } else {
        if (this.bidApi.data?.message) {
          alert(this.bidApi.data.message)
        }
      }

      this.$emit('fetchBids')
    }
  }
}
</script>
